import React, { useState } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

/**
 * EnableCustomerStep - Step 9: Enable the customer (set is_iris_enabled=true in IDC).
 */
const EnableCustomerStep = ({ wizard, api }) => {
  const [result, setResult] = useState(null);
  const [skipped, setSkipped] = useState(false);
  const navigate = useNavigate();

  const handleEnable = async () => {
    try {
      const data = await api.enableCustomer(wizard.sessionId);
      setResult(data);
      wizard.completeStep(9);
    } catch {
      // Error shown by hook
    }
  };

  const handleSkip = () => {
    setSkipped(true);
    wizard.completeStep(9);
  };

  const handleViewCustomer = () => {
    navigate(`/customers/${wizard.customerId}`);
  };

  const handleBackToCustomers = () => {
    navigate('/customers');
  };

  if (result) {
    return (
      <div>
        <Alert variant={result.enabled ? 'success' : 'warning'}>
          <Alert.Heading>{result.enabled ? 'Customer Enabled' : 'Enable Failed'}</Alert.Heading>
          <p>{result.message}</p>
        </Alert>
        {result.enabled && (
          <div className="mt-3">
            <Alert variant="light" className="border">
              <h6>✅ Provisioning Complete!</h6>
              <p className="mb-0">The customer has been fully provisioned and enabled. Invoice processing is now active.</p>
            </Alert>
            <div className="d-flex gap-2 mt-3">
              <Button variant="primary" onClick={handleViewCustomer}>
                View Customer
              </Button>
              <Button variant="outline-secondary" onClick={handleBackToCustomers}>
                Back to Customer Management
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (skipped) {
    return (
      <div>
        <Alert variant="info">
          <Alert.Heading>Provisioning Complete (Not Enabled)</Alert.Heading>
          <p>The customer has been provisioned but is not yet enabled. You can enable them later from the customer detail page using the toggle.</p>
        </Alert>
        <div className="d-flex gap-2 mt-3">
          <Button variant="primary" onClick={handleViewCustomer}>
            View Customer
          </Button>
          <Button variant="outline-secondary" onClick={handleBackToCustomers}>
            Back to Customer Management
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h5>Step 9: Enable Customer</h5>
      <p className="text-muted">
        This will set <code>is_iris_enabled = true</code> in the InvoiceDataCapture job records, activating the customer for processing.
      </p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}

      <Alert variant="info">
        <strong>What happens when you enable:</strong>
        <ul className="mb-0 mt-1">
          <li>IDC job records will have is_iris_enabled set to true</li>
          <li>The customer will begin receiving invoices for processing</li>
          <li>You can disable later from the customer detail page</li>
        </ul>
      </Alert>

      <div className="d-flex gap-2">
        <Button variant="primary" onClick={handleEnable} disabled={api.loading}>
          {api.loading ? <><Spinner size="sm" className="me-2" />Enabling...</> : 'Enable Now'}
        </Button>
        <Button variant="outline-secondary" onClick={handleSkip} disabled={api.loading}>
          Enable Later
        </Button>
      </div>
    </div>
  );
};

export default EnableCustomerStep;
